// Optimized 2D water ripple effect using a low-resolution buffer and plain Canvas API.
// - draws an overlay canvas positioned absolutely over the container
// - uses a small offscreen buffer for simulation (faster)
// - responds to mousemove on the container (not clicks)
// - returns a handle with `destroy()` to stop animation and listeners

export const initWaterRipple = (containerSelector = '.hero', opts = {}) => {
  const container = document.querySelector(containerSelector);
  if (!container) return null;

  const scale = opts.scale || 3; // downscale factor for simulation (higher = faster)
  const dampening = typeof opts.dampening === 'number' ? opts.dampening : 0.99;
  const intensity = typeof opts.intensity === 'number' ? opts.intensity : 600;
  const radius = typeof opts.radius === 'number' ? Math.max(1, Math.floor(opts.radius)) : 6; // in buffer pixels

  // ensure container can position children absolutely
  const cs = window.getComputedStyle(container);
  if (cs.position === 'static') container.style.position = 'relative';

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.style.position = 'absolute';
  canvas.style.left = '0';
  canvas.style.top = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.pointerEvents = 'none';
  canvas.style.mixBlendMode = opts.blend || 'overlay';
  container.appendChild(canvas);

  let width = container.clientWidth || 600;
  let height = container.clientHeight || 400;
  canvas.width = width;
  canvas.height = height;

  let bufW = Math.max(2, Math.floor(width / scale));
  let bufH = Math.max(2, Math.floor(height / scale));

  const offCanvas = document.createElement('canvas');
  offCanvas.width = bufW;
  offCanvas.height = bufH;
  const offCtx = offCanvas.getContext('2d');
  let imageData = offCtx.createImageData(bufW, bufH);

  let prev = new Float32Array(bufW * bufH);
  let curr = new Float32Array(bufW * bufH);

  let rafId = null;

  function resetBuffers() {
    width = container.clientWidth || 600;
    height = container.clientHeight || 400;
    canvas.width = width;
    canvas.height = height;
    bufW = Math.max(2, Math.floor(width / scale));
    bufH = Math.max(2, Math.floor(height / scale));
    offCanvas.width = bufW;
    offCanvas.height = bufH;
    imageData = offCtx.createImageData(bufW, bufH);
    prev = new Float32Array(bufW * bufH);
    curr = new Float32Array(bufW * bufH);
  }

  function disturb(bx, by, val = intensity) {
    if (bx < 1 || bx >= bufW - 1 || by < 1 || by >= bufH - 1) return;
    const r = Math.max(1, radius);
    const r2 = r * r;
    const startX = Math.max(1, bx - r);
    const endX = Math.min(bufW - 2, bx + r);
    const startY = Math.max(1, by - r);
    const endY = Math.min(bufH - 2, by + r);
    for (let yy = startY; yy <= endY; yy++) {
      for (let xx = startX; xx <= endX; xx++) {
        const dx = xx - bx;
        const dy = yy - by;
        const d2 = dx * dx + dy * dy;
        if (d2 <= r2) {
          const d = Math.sqrt(d2);
          const falloff = 1 - d / (r + 0.1);
          prev[xx + yy * bufW] = Math.max(prev[xx + yy * bufW], val * falloff);
        }
      }
    }
  }

  let lastMove = 0;
  function onMove(e) {
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const bx = Math.floor((x / Math.max(1, width)) * bufW);
    const by = Math.floor((y / Math.max(1, height)) * bufH);
    disturb(bx, by, intensity);
    lastMove = performance.now();
  }

  container.addEventListener('mousemove', onMove);

  function step() {
    // Simulation: iterate inner pixels
    for (let y = 1; y < bufH - 1; y++) {
      let row = y * bufW;
      let rowUp = row - bufW;
      let rowDown = row + bufW;
      for (let x = 1; x < bufW - 1; x++) {
        const i = row + x;
        curr[i] =
          ((prev[i - 1] + prev[i + 1] + prev[rowUp + x] + prev[rowDown + x]) / 1.995 - curr[i]) *
          dampening;
      }
    }

    // build imageData (visualization)
    const data = imageData.data;
    for (let i = 0, j = 0; i < prev.length; i++, j += 4) {
      const v = curr[i];
      // gray mid-tone with subtle alpha based on amplitude
      const shade = 0 + Math.round(Math.max(-120, Math.min(120, v * 0.25)));
      const alpha = Math.min(18, Math.floor(Math.abs(v) * 0.25));
      data[j] = shade;
      data[j + 1] = shade;
      data[j + 2] = shade;
      data[j + 3] = alpha;
    }

    offCtx.putImageData(imageData, 0, 0);

    // clear main canvas and draw scaled up offscreen buffer
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(offCanvas, 0, 0, width, height);

    // swap buffers
    const temp = prev;
    prev = curr;
    curr = temp;

    rafId = window.requestAnimationFrame(step);
  }

  // Start animation
  rafId = window.requestAnimationFrame(step);

  // resize observer for container
  const ro = new ResizeObserver(() => {
    cancelAnimationFrame(rafId);
    resetBuffers();
    rafId = window.requestAnimationFrame(step);
  });
  ro.observe(container);

  return {
    destroy() {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      container.removeEventListener('mousemove', onMove);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    },
  };
};
